import type {
  ChatPlayerAuthoredMessage,
  ChatPlayerScenarioSource,
  ChatPlayerSource,
  ChatPlayerTranscriptSource,
} from "@ui/components/core/ChatPlayer";

const DEFAULT_MESSAGE_INTERVAL_MS = 1000;

export type AccordionConversationMessage = {
  role?: ("user" | "agent") | null;
  message?: string | null;
  delay?: number | null;
};

export type AccordionConversationSourceOptions = {
  syncToVideo?: boolean;
};

export function getVideoConversationMessageStartMs(messages: AccordionConversationMessage[], index: number): number {
  const message = messages[index];
  if (!message) {
    return index * DEFAULT_MESSAGE_INTERVAL_MS;
  }

  if (typeof message.delay === "number") {
    return Math.max(message.delay, 0);
  }

  if (index === 0) {
    return 0;
  }

  return getVideoConversationMessageStartMs(messages, index - 1) + DEFAULT_MESSAGE_INTERVAL_MS;
}

function filterAccordionConversationMessages(messages: AccordionConversationMessage[]) {
  return messages.filter(message => message.message?.trim().length);
}

function mapRole(role: AccordionConversationMessage["role"]) {
  return role === "agent" ? "assistant" : "user";
}

function mapSpeaker(role: AccordionConversationMessage["role"]) {
  return role === "agent" ? "agent" : "user";
}

function createScenarioSource(filtered: AccordionConversationMessage[]) {
  return {
    id: "accordion-carousel-conversation",
    messages: filtered.map(
      (message, index) =>
        ({
          id: `accordion-message-${index}`,
          role: mapRole(message.role),
          parts: [
            {
              id: `accordion-message-${index}-text`,
              type: "text",
              content: message.message!.trim(),
            },
          ],
        }) satisfies ChatPlayerAuthoredMessage,
    ),
  } satisfies ChatPlayerScenarioSource;
}

function createVideoTranscriptSource(filtered: AccordionConversationMessage[]) {
  const starts = filtered.map((_, index) => getVideoConversationMessageStartMs(filtered, index));

  return {
    id: "accordion-carousel-conversation-video",
    speakerRoles: {
      agent: "assistant",
      user: "user",
    },
    segments: filtered.map((message, index) => {
      const startMs = starts[index] ?? 0;

      return {
        id: `accordion-message-${index}`,
        text: message.message!.trim(),
        startMs,
        endMs: startMs,
        speaker: mapSpeaker(message.role),
      };
    }),
  } satisfies ChatPlayerTranscriptSource;
}

export function createChatPlayerSourceFromAccordionConversation(
  messages: AccordionConversationMessage[] | null | undefined,
  options?: AccordionConversationSourceOptions,
): ChatPlayerSource | null {
  const filtered = filterAccordionConversationMessages(messages ?? []);

  if (filtered.length === 0) {
    return null;
  }

  if (options?.syncToVideo) {
    return createVideoTranscriptSource(filtered);
  }

  return createScenarioSource(filtered);
}
